import { useEffect, useRef, useCallback, useState } from 'react'
import { useEditorStore } from '@/stores/useEditorStore'
import { useProjectStore } from '@/stores/useProjectStore'
import { setCanvasInstance } from '@/lib/canvasInstance'
import {
    copySelected,
    pasteFromClipboard,
    cutSelected,
    duplicateSelected,
    selectAll,
    nudgeSelected,
    deleteSelected,
    deselectAll,
} from '@/lib/canvasShortcuts'
import { getClipboard } from '@/lib/canvasClipboard'
import { ContextMenu } from '@/components/editor/ContextMenu/ContextMenu'
import styles from './EditorCanvas.module.css'

interface EditorCanvasProps {
    onCanvasReady: (canvas: unknown) => void
}

const MM_TO_PX = 3.7795

/** Custom properties that must survive fabric.js serialization. */
const CUSTOM_PROPS = ['_isPageRect']

export function EditorCanvas({ onCanvasReady }: EditorCanvasProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fabricRef = useRef<any>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const isPanningRef = useRef(false)
    const lastPosRef = useRef({ x: 0, y: 0 })
    const spaceHeldRef = useRef(false)
    const zoomAppliedRef = useRef(false)

    const { zoom, setZoom, currentPageIndex, pushUndo, setSelectedObjects } = useEditorStore()
    const { widthMm, heightMm, pages } = useProjectStore()
    const [isPanning, setIsPanning] = useState(false)
    const [ctxMenu, setCtxMenu] = useState({ visible: false, x: 0, y: 0, hasSelection: false })

    const docWidth = Math.round(widthMm * MM_TO_PX)
    const docHeight = Math.round(heightMm * MM_TO_PX)

    const saveCanvasToStore = useCallback(() => {
        if (!fabricRef.current) return
        const canvas = fabricRef.current
        const json = canvas.toJSON(CUSTOM_PROPS)
        const idx = useEditorStore.getState().currentPageIndex
        useProjectStore.getState().updatePage(idx, json)
        useEditorStore.getState().setIsDirty(true)
    }, [])

    const initCanvas = useCallback(async () => {
        if (!containerRef.current) return

        // Dispose previous
        if (fabricRef.current) {
            fabricRef.current.dispose()
            fabricRef.current = null
        }

        const container = containerRef.current
        const cw = container.clientWidth || 800
        const ch = container.clientHeight || 600

        // Create fresh canvas element
        const oldCanvas = canvasRef.current
        if (oldCanvas) oldCanvas.remove()
        const freshCanvas = document.createElement('canvas')
        container.appendChild(freshCanvas)
        canvasRef.current = freshCanvas

        try {
            const fabric = await import('fabric')

            const canvas = new fabric.Canvas(freshCanvas, {
                width: cw,
                height: ch,
                backgroundColor: 'transparent',
                selection: true,
                preserveObjectStacking: true,
                controlsAboveOverlay: true,
            })

            // Default object styling
            fabric.FabricObject.prototype.set({
                cornerColor: '#4ea89a',
                cornerStyle: 'circle',
                cornerSize: 6,
                transparentCorners: false,
                borderColor: '#4ea89a',
                borderScaleFactor: 1,
                padding: 2,
            })

            // Load existing canvas data OR create blank page
            const currentPage = pages[currentPageIndex]
            const hasContent = currentPage?.canvas_json && Object.keys(currentPage.canvas_json).length > 0

            if (hasContent) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const sanitizedJson = { ...currentPage.canvas_json } as any
                if (Array.isArray(sanitizedJson.objects)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    sanitizedJson.objects = sanitizedJson.objects.map((obj: any) => {
                        // Normalize image type casing for Fabric.js 6.x
                        if (obj.type === 'Image') {
                            return { ...obj, type: 'image' }
                        }
                        return obj
                    })
                }

                await canvas.loadFromJSON(sanitizedJson)
                canvas.renderAll()

                // Reconcile page rect: find it by _isPageRect (if serialized)
                // or by matching characteristics, remove stale ghost rects
                reconcilePageRect(canvas, fabric, docWidth, docHeight)
            } else {
                const pageRect = createPageRect(fabric, docWidth, docHeight)
                canvas.add(pageRect)
                canvas.sendObjectToBack(pageRect)
            }

            // ── Event handlers ──

            canvas.on('object:modified', () => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                pushUndo(JSON.stringify((canvas as any).toJSON(CUSTOM_PROPS)))
                saveCanvasToStore()
            })
            canvas.on('object:added', () => saveCanvasToStore())
            canvas.on('object:removed', () => saveCanvasToStore())

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            canvas.on('selection:created', (e: any) => {
                const ids = (e.selected || []).map((o: { id?: string }) => String(o.id || Math.random())).filter(Boolean)
                setSelectedObjects(ids)
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            canvas.on('selection:updated', (e: any) => {
                const ids = (e.selected || []).map((o: { id?: string }) => String(o.id || Math.random())).filter(Boolean)
                setSelectedObjects(ids)
            })
            canvas.on('selection:cleared', () => setSelectedObjects([]))

            // Middle-click or space+drag panning
            canvas.on('mouse:down', (opt) => {
                const e = opt.e as MouseEvent
                if (e.button === 1 || spaceHeldRef.current) {
                    isPanningRef.current = true
                    lastPosRef.current = { x: e.clientX, y: e.clientY }
                    canvas.selection = false
                    setIsPanning(true)
                }
            })

            canvas.on('mouse:move', (opt) => {
                if (!isPanningRef.current) return
                const e = opt.e as MouseEvent
                const vpt = canvas.viewportTransform
                if (!vpt) return
                vpt[4] += e.clientX - lastPosRef.current.x
                vpt[5] += e.clientY - lastPosRef.current.y
                lastPosRef.current = { x: e.clientX, y: e.clientY }
                canvas.requestRenderAll()
            })

            canvas.on('mouse:up', () => {
                if (isPanningRef.current) {
                    isPanningRef.current = false
                    canvas.selection = true
                    setIsPanning(false)
                    canvas.setViewportTransform(canvas.viewportTransform!)
                }
                canvas.requestRenderAll()
            })

            // Store reference
            fabricRef.current = canvas
            setCanvasInstance(canvas)
            onCanvasReady(canvas)

            // Center the document in the viewport
            const initialZoom = useEditorStore.getState().zoom
            const offsetX = (cw - docWidth * initialZoom) / 2
            const offsetY = (ch - docHeight * initialZoom) / 2
            canvas.setViewportTransform([initialZoom, 0, 0, initialZoom, offsetX, offsetY])
            canvas.requestRenderAll()
        } catch (error) {
            console.error('Failed to initialize Fabric.js canvas:', error)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docWidth, docHeight, currentPageIndex])

    useEffect(() => {
        initCanvas()
        return () => {
            if (fabricRef.current) {
                setCanvasInstance(null)
                fabricRef.current.dispose()
                fabricRef.current = null
            }
            if (canvasRef.current) {
                canvasRef.current.remove()
                canvasRef.current = null
            }
        }
    }, [initCanvas])

    // Resize
    useEffect(() => {
        const container = containerRef.current
        if (!container) return
        const observer = new ResizeObserver(() => {
            if (!fabricRef.current) return
            fabricRef.current.setDimensions({
                width: container.clientWidth,
                height: container.clientHeight,
            })
            fabricRef.current.requestRenderAll()
        })
        observer.observe(container)
        return () => observer.disconnect()
    }, [])

    // Zoom from ZoomControls
    useEffect(() => {
        if (!fabricRef.current || !containerRef.current) return
        if (zoomAppliedRef.current) {
            zoomAppliedRef.current = false
            return
        }
        const canvas = fabricRef.current
        const cw = containerRef.current.clientWidth
        const ch = containerRef.current.clientHeight
        canvas.zoomToPoint({ x: cw / 2, y: ch / 2 }, zoom)
        canvas.requestRenderAll()
    }, [zoom])

    // Ctrl+Scroll = zoom, middle-click suppression, pinch-to-zoom
    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault()
            if (!fabricRef.current) return
            if (!(e.ctrlKey || e.metaKey)) return

            const canvas = fabricRef.current
            const currentZoom = useEditorStore.getState().zoom
            const factor = e.deltaY < 0 ? 1.1 : 0.9
            const newZoom = Math.max(0.1, Math.min(5, currentZoom * factor))
            const rect = container.getBoundingClientRect()
            canvas.zoomToPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top }, newZoom)
            zoomAppliedRef.current = true
            setZoom(newZoom)
            canvas.requestRenderAll()
        }

        const handleAuxClick = (e: MouseEvent) => {
            if (e.button === 1) e.preventDefault()
        }

        // Pinch-to-zoom for mobile
        let lastPinchDist = 0
        let lastPinchCenter = { x: 0, y: 0 }
        let pinchActive = false

        const getTouchDist = (t1: Touch, t2: Touch) =>
            Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)

        const getTouchCenter = (t1: Touch, t2: Touch) => ({
            x: (t1.clientX + t2.clientX) / 2,
            y: (t1.clientY + t2.clientY) / 2,
        })

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault()
                pinchActive = true
                lastPinchDist = getTouchDist(e.touches[0], e.touches[1])
                lastPinchCenter = getTouchCenter(e.touches[0], e.touches[1])
            }
        }

        const handleTouchMove = (e: TouchEvent) => {
            if (!pinchActive || e.touches.length !== 2 || !fabricRef.current) return
            e.preventDefault()

            const canvas = fabricRef.current
            const dist = getTouchDist(e.touches[0], e.touches[1])
            const center = getTouchCenter(e.touches[0], e.touches[1])
            const rect = container.getBoundingClientRect()

            // Zoom
            const scale = dist / lastPinchDist
            const currentZoom = useEditorStore.getState().zoom
            const newZoom = Math.max(0.1, Math.min(5, currentZoom * scale))
            const point = { x: center.x - rect.left, y: center.y - rect.top }
            canvas.zoomToPoint(point, newZoom)
            zoomAppliedRef.current = true
            setZoom(newZoom)

            // Pan
            const vpt = canvas.viewportTransform
            if (vpt) {
                vpt[4] += center.x - lastPinchCenter.x
                vpt[5] += center.y - lastPinchCenter.y
            }

            lastPinchDist = dist
            lastPinchCenter = center
            canvas.requestRenderAll()
        }

        const handleTouchEnd = (e: TouchEvent) => {
            if (e.touches.length < 2) {
                pinchActive = false
            }
        }

        container.addEventListener('wheel', handleWheel, { passive: false })
        container.addEventListener('auxclick', handleAuxClick)
        container.addEventListener('touchstart', handleTouchStart, { passive: false })
        container.addEventListener('touchmove', handleTouchMove, { passive: false })
        container.addEventListener('touchend', handleTouchEnd)
        return () => {
            container.removeEventListener('wheel', handleWheel)
            container.removeEventListener('auxclick', handleAuxClick)
            container.removeEventListener('touchstart', handleTouchStart)
            container.removeEventListener('touchmove', handleTouchMove)
            container.removeEventListener('touchend', handleTouchEnd)
        }
    }, [setZoom])

    // Drag-and-drop from sidebar
    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault()
        const canvas = fabricRef.current
        if (!canvas || !containerRef.current) return

        const raw = e.dataTransfer.getData('application/x-editor-element')
        if (!raw) return

        let payload: { type: string; config?: Record<string, unknown> }
        try { payload = JSON.parse(raw) } catch { return }

        const rect = containerRef.current.getBoundingClientRect()
        const vpt = canvas.viewportTransform
        const canvasX = (e.clientX - rect.left - vpt[4]) / vpt[0]
        const canvasY = (e.clientY - rect.top - vpt[5]) / vpt[3]

        const fabric = await import('fabric')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let obj: any = null

        if (payload.type === 'text') {
            const cfg = payload.config || {}
            obj = new fabric.IText((cfg.text as string) || 'Texto', {
                left: canvasX, top: canvasY,
                fontSize: (cfg.fontSize as number) || 18,
                fontWeight: (cfg.fontWeight as string) || 'normal',
                fontFamily: (cfg.fontFamily as string) || 'Arial',
                fill: '#171c1c', editable: true,
            })
        } else if (payload.type === 'shape') {
            const shapeId = (payload.config?.shapeId as string) || 'rect'
            const bp = { left: canvasX, top: canvasY, fill: '#add9d1', stroke: '#4ea89a', strokeWidth: 1 }
            switch (shapeId) {
                case 'rect': obj = new fabric.Rect({ ...bp, width: 150, height: 100, rx: 8, ry: 8 }); break
                case 'circle': obj = new fabric.Circle({ ...bp, radius: 60 }); break
                case 'line': obj = new fabric.Line([0, 0, 200, 0], { ...bp, stroke: '#4ea89a', strokeWidth: 3 }); break
                case 'triangle': obj = new fabric.Triangle({ ...bp, width: 120, height: 120 }); break
                case 'star': obj = new fabric.Polygon(createStarPoints(5, 60, 30), { ...bp }); break
            }
        }

        if (obj) {
            canvas.add(obj)
            canvas.setActiveObject(obj)
            canvas.requestRenderAll()
            pushUndo(JSON.stringify(canvas.toJSON(CUSTOM_PROPS)))
        }
    }, [pushUndo])

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
    }, [])

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const canvas = fabricRef.current
            if (!canvas) return

            const active = canvas.getActiveObject()
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (active && (active as any).isEditing) {
                if (e.key === 'Escape') {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ;(active as any).exitEditing()
                    canvas.requestRenderAll()
                }
                return
            }

            const isCtrl = e.ctrlKey || e.metaKey

            if (isCtrl) {
                switch (e.key.toLowerCase()) {
                    case 'c': e.preventDefault(); copySelected(); return
                    case 'v': e.preventDefault(); pasteFromClipboard(); return
                    case 'x': e.preventDefault(); cutSelected(); return
                    case 'd': e.preventDefault(); duplicateSelected(); return
                    case 'a': e.preventDefault(); selectAll(); return
                    case 'z':
                        e.preventDefault()
                        if (e.shiftKey) {
                            const json = useEditorStore.getState().redo()
                            if (json) canvas.loadFromJSON(JSON.parse(json)).then(() => canvas.renderAll())
                        } else {
                            const json = useEditorStore.getState().undo()
                            if (json) canvas.loadFromJSON(JSON.parse(json)).then(() => canvas.renderAll())
                        }
                        return
                    case 's': e.preventDefault(); useEditorStore.getState().setIsDirty(true); return
                    case '0':
                        e.preventDefault()
                        if (containerRef.current) {
                            const cw = containerRef.current.clientWidth
                            const ch = containerRef.current.clientHeight
                            const fitZoom = Math.min((cw - 100) / docWidth, (ch - 100) / docHeight, 1)
                            zoomAppliedRef.current = true
                            canvas.setViewportTransform([fitZoom, 0, 0, fitZoom, (cw - docWidth * fitZoom) / 2, (ch - docHeight * fitZoom) / 2])
                            setZoom(fitZoom)
                            canvas.requestRenderAll()
                        }
                        return
                }
            }

            switch (e.key) {
                case 'Delete': case 'Backspace': e.preventDefault(); deleteSelected(); return
                case 'Escape': deselectAll(); setCtxMenu(p => ({ ...p, visible: false })); return
                case 'ArrowUp': e.preventDefault(); nudgeSelected(0, e.shiftKey ? -10 : -1); return
                case 'ArrowDown': e.preventDefault(); nudgeSelected(0, e.shiftKey ? 10 : 1); return
                case 'ArrowLeft': e.preventDefault(); nudgeSelected(e.shiftKey ? -10 : -1, 0); return
                case 'ArrowRight': e.preventDefault(); nudgeSelected(e.shiftKey ? 10 : 1, 0); return
            }

            if (e.code === 'Space' && !e.repeat) {
                spaceHeldRef.current = true
                setIsPanning(true)
            }
        }

        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.code === 'Space') { spaceHeldRef.current = false; setIsPanning(false) }
        }

        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('keyup', handleKeyUp)
        return () => { window.removeEventListener('keydown', handleKeyDown); window.removeEventListener('keyup', handleKeyUp) }
    }, [pushUndo, docWidth, docHeight, setZoom])

    const handleContextMenu = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        const canvas = fabricRef.current
        setCtxMenu({ visible: true, x: e.clientX, y: e.clientY, hasSelection: canvas ? !!canvas.getActiveObject() : false })
    }, [])

    return (
        <>
            <div
                className={`${styles.canvasContainer} ${isPanning ? styles.panning : ''}`}
                ref={containerRef}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onContextMenu={handleContextMenu}
            />
            <ContextMenu
                x={ctxMenu.x} y={ctxMenu.y}
                visible={ctxMenu.visible}
                onClose={() => setCtxMenu(p => ({ ...p, visible: false }))}
                hasSelection={ctxMenu.hasSelection}
                hasClipboard={!!getClipboard()}
            />
        </>
    )
}

/**
 * After loadFromJSON, ensure there is exactly ONE page rect at (0,0)
 * with the correct document dimensions. Removes stale ghost rects
 * left from previous document types or resizes.
 *
 * Detection heuristic for old data where _isPageRect was not serialized:
 * a Rect is a "page-rect-like" if it has white fill, the same shadow/stroke
 * style, and is non-selectable OR sits at (0,0) with the page shadow.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function reconcilePageRect(canvas: any, fabric: any, docW: number, docH: number) {
    const objects = canvas.getObjects()

    // 1. Find objects already tagged
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let tagged = objects.filter((o: any) => o._isPageRect)

    if (tagged.length === 0) {
        // 2. No tagged rects — identify page-rect-like Rects by shape:
        //    white fill, shadow, non-selectable or at origin
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const candidates = objects.filter((o: any) => {
            if (o.type !== 'Rect' && o.type !== 'rect') return false
            if (o.fill !== '#ffffff') return false
            if (o.stroke !== '#d0d0d0') return false
            if (o.shadow) return true   // has the page shadow → likely page rect
            return false
        })

        if (candidates.length > 0) {
            // Pick the one closest to (0,0) with current doc dimensions as the real one
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const exact = candidates.find((o: any) =>
                Math.abs(o.left) < 1 && Math.abs(o.top) < 1 &&
                Math.abs(o.width - docW) < 2 && Math.abs(o.height - docH) < 2
            )

            if (exact) {
                exact._isPageRect = true
                exact.set({ selectable: false, evented: false })
                tagged = [exact]
            } else {
                // No exact match — all candidates are stale ghosts
                tagged = []
            }

            // Remove all non-tagged candidates (ghost rects from old doc types)
            for (const c of candidates) {
                if (!c._isPageRect) {
                    canvas.remove(c)
                }
            }
        }
    }

    if (tagged.length === 0) {
        // 3. No page rect found at all — create one
        const pr = createPageRect(fabric, docW, docH)
        canvas.add(pr)
        canvas.sendObjectToBack(pr)
    } else {
        // 4. Ensure the tagged page rect has correct dimensions & position
        const pr = tagged[0]
        pr.set({
            left: 0, top: 0,
            width: docW, height: docH,
            scaleX: 1, scaleY: 1,
            selectable: false, evented: false,
        })
        pr._isPageRect = true
        canvas.sendObjectToBack(pr)

        // Remove any extra tagged rects (shouldn't happen, but safety)
        for (let i = 1; i < tagged.length; i++) {
            canvas.remove(tagged[i])
        }
    }

    canvas.renderAll()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createPageRect(fabric: any, w: number, h: number) {
    const rect = new fabric.Rect({
        left: 0, top: 0, width: w, height: h,
        fill: '#ffffff', stroke: '#d0d0d0', strokeWidth: 1,
        selectable: false, evented: false, excludeFromExport: false,
        shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.12)', blur: 16, offsetX: 0, offsetY: 4 }),
    })
    rect._isPageRect = true
    return rect
}

function createStarPoints(spikes: number, outerR: number, innerR: number) {
    const pts = []
    let rot = (Math.PI / 2) * 3
    const step = Math.PI / spikes
    for (let i = 0; i < spikes; i++) {
        pts.push({ x: outerR + Math.cos(rot) * outerR, y: outerR + Math.sin(rot) * outerR })
        rot += step
        pts.push({ x: outerR + Math.cos(rot) * innerR, y: outerR + Math.sin(rot) * innerR })
        rot += step
    }
    return pts
}
